import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
import RelationPicker from './RelationPicker';
import AudioPlaylistPicker, { MAX_AUDIO_TRACKS } from './AudioPlaylistPicker';
import AartiSchedulePicker from './AartiSchedulePicker';
import { _axios } from '../../utils/axios';

const STREAM_CATEGORIES = [
    { value: 'live_darshan', label: 'Deity Darshan' },
    { value: 'live_podcasts', label: 'Podcast' },
    { value: 'live_discourse', label: 'Discourse' },
    { value: 'live_aarti', label: 'Aarti' },
    { value: 'live_event_coverage', label: 'Event Coverage' },
    { value: 'live_pooja', label: 'Pooja' },
];

const getStreamStatusBadgeConfig = (status) => ({
    draft: { label: 'Draft', bg: '#9E9E9E' },
    live_now: { label: 'Live Now', bg: '#4CAF50' },
    processing: { label: 'Processing', bg: '#FF9800' },
    recently_live: { label: 'Recently Live', bg: '#2196F3' },
    archived: { label: 'Archived', bg: '#424242' },
}[status] || { label: status || 'Draft', bg: '#9E9E9E' });

// NOTE: the backend currently mislabels these as UTC ('...Z') when they are
// actually already IST digits (a server-side mysql2/timezone bug — see
// parikrama-backend). So we deliberately do NOT run this through Date/Intl
// timezone conversion (that would double-shift it +5:30 again) — we just
// read the digits off the string as-is. If the backend bug is ever fixed to
// return true UTC, this must switch back to a real conversion or it'll be
// off by 5:30 in the other direction.
const toISTInput = (utcStr) => {
    if (!utcStr) return '';
    const match = String(utcStr).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return '';
    const [, y, mo, d, h, mi] = match;
    return `${y}-${mo}-${d}T${h}:${mi}`;
};

const MAX_ANIMATIONS = 5;

const AnimationSection = ({ formData, setFormData, inputStyle, labelStyle }) => {
    const enabled = formData.enable_animation === 1;
    const urls = formData.animation_urls && formData.animation_urls.length ? formData.animation_urls : [''];

    const updateUrl = (idx, value) => {
        setFormData(prev => {
            const next = [...(prev.animation_urls && prev.animation_urls.length ? prev.animation_urls : [''])];
            next[idx] = value;
            return { ...prev, animation_urls: next };
        });
    };

    const removeUrl = (idx) => {
        setFormData(prev => {
            const next = (prev.animation_urls || []).filter((_, i) => i !== idx);
            return { ...prev, animation_urls: next.length ? next : [''] };
        });
    };

    const addUrl = () => {
        setFormData(prev => {
            const cur = prev.animation_urls && prev.animation_urls.length ? prev.animation_urls : [''];
            if (cur.length >= MAX_ANIMATIONS) return prev;
            return { ...prev, animation_urls: [...cur, ''] };
        });
    };

    return (
        <Row className='mb-3'>
            <Col md={12}>
                <div style={{
                    padding: '14px 18px', borderRadius: '10px',
                    border: '1px solid #E2E8F0', background: '#FAFAFA',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enabled ? '14px' : '0' }}>
                        <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#4A5568', marginBottom: '3px' }}>
                                Enable Live Stream Animation
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096' }}>
                                Show a short animation before the live stream opens. Add up to {MAX_ANIMATIONS} R2 CDN links.
                            </div>
                        </div>
                        <Form.Check
                            type='switch'
                            id='edit_enable_animation_switch'
                            checked={enabled}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                enable_animation: e.target.checked ? 1 : 0,
                                animation_urls: e.target.checked ? (prev.animation_urls && prev.animation_urls.length ? prev.animation_urls : ['']) : [],
                            }))}
                            style={{ fontSize: '16px' }}
                        />
                    </div>

                    {enabled && (
                        <>
                            <Form.Label className='mb-2' style={{ ...labelStyle, fontSize: '14px' }}>
                                Select Animation <span style={{ fontSize: '12px', color: '#A0AEC0', fontWeight: 400 }}>({urls.filter(u => u.trim()).length}/{MAX_ANIMATIONS})</span>
                            </Form.Label>
                            {urls.map((url, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <Form.Control
                                        type='text'
                                        value={url}
                                        onChange={(e) => updateUrl(idx, e.target.value)}
                                        placeholder='https://your-r2-bucket.r2.dev/animations/lotus.mp4'
                                        style={inputStyle}
                                    />
                                    {url.trim() && (
                                        <a href={url} target='_blank' rel='noopener noreferrer'
                                            style={{ fontSize: '13px', color: '#5662F6', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                            Open
                                        </a>
                                    )}
                                    <Button
                                        variant='outline-danger'
                                        size='sm'
                                        onClick={() => removeUrl(idx)}
                                        disabled={urls.length === 1 && !url}
                                        style={{ padding: '4px 10px' }}
                                    >
                                        ×
                                    </Button>
                                </div>
                            ))}
                            <Button
                                variant='outline-primary'
                                size='sm'
                                onClick={addUrl}
                                disabled={urls.length >= MAX_ANIMATIONS}
                                style={{ marginTop: '4px' }}
                            >
                                + Add Animation
                            </Button>
                        </>
                    )}
                </div>
            </Col>
        </Row>
    );
};

const AudioSection = ({
    formData,
    setFormData,
    audioArtifacts,
    searchArtifact,
    setSearchArtifact,
    onFetchArtifacts,
    artifactsFetched,
    selectedAudioArtifacts,
    setSelectedAudioArtifacts,
}) => {
    const isOriginalSound = formData.original_sound === 1;
    return (
        <>
            <hr style={{ margin: '8px 0 16px', borderColor: '#E2E8F0' }} />
            <div style={{ marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', fontSize: '15px', color: '#4A5568' }}>Audio</span>
            </div>
            <Row className='mb-2'>
                <Col md={3} style={{ display: 'flex', alignItems: 'center' }}>
                    <Form.Check type='checkbox' label='Original Sound'
                        checked={isOriginalSound}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData(prev => ({ ...prev, original_sound: checked ? 1 : 0 }));
                            if (checked) setSelectedAudioArtifacts([]);
                        }}
                        style={{ fontSize: '16px' }} />
                </Col>
            </Row>
            {!isOriginalSound && (
                <AudioPlaylistPicker
                    audioArtifacts={audioArtifacts}
                    searchValue={searchArtifact}
                    onSearchChange={setSearchArtifact}
                    onFetchArtifacts={onFetchArtifacts}
                    artifactsFetched={artifactsFetched}
                    selected={selectedAudioArtifacts}
                    onChange={setSelectedAudioArtifacts}
                />
            )}
        </>
    );
};

const EditLiveStream = ({ show, onHide, streamData }) => {
    const [formData, setFormData] = useState({
        stream_type: 'youtube',
        stream_category: 'live_darshan',
        youtube_video_id: '',
        stream_key: '',
        rtsp_url: '',
        hls_url: '',
        stream_title: '',
        stream_title_mar: '',
        stream_title_hin: '',
        stream_title_guj: '',
        thumbnail_url: '',
        is_featured: false,
        enable_multi_view: false,
        stream_status: 'draft',
        auto_convert: 0,
        converted_artifact_id: null,
        converted_r2_url: null,
        stream_mode: 'rtmp_push',
        orientation: 'portrait',
        max_quality: '1080p',
        display_sponsor: false,
        sponsor_1_name: '',
        sponsor_1_name_mar: '',
        sponsor_1_name_hin: '',
        sponsor_1_name_guj: '',
        sponsor_2_name: '',
        sponsor_2_name_mar: '',
        sponsor_2_name_hin: '',
        sponsor_2_name_guj: '',
        sponsor_1_image: streamData.sponsor1Image || '',
        sponsor_1_info: streamData.sponsor1Info || '',
        sponsor_1_info_mar: streamData.sponsor1InfoMar || '',
        sponsor_1_info_hin: streamData.sponsor1InfoHin || '',
        sponsor_1_info_guj: streamData.sponsor1InfoGuj || '',
        sponsor_2_image: streamData.sponsor2Image || '',
        sponsor_2_info: streamData.sponsor2Info || '',
        sponsor_2_info_mar: streamData.sponsor2InfoMar || '',
        sponsor_2_info_hin: streamData.sponsor2InfoHin || '',
        sponsor_2_info_guj: streamData.sponsor2InfoGuj || '',
        stream_start_at: '',
        stream_end_at: '',
        original_sound: 1,
        enable_animation: 0,
        animation_urls: [],
    });

    const [audioArtifacts, setAudioArtifacts] = useState([]);
    const [artifactsFetched, setArtifactsFetched] = useState(false);
    const [searchArtifact, setSearchArtifact] = useState('');
    const [selectedAudioArtifacts, setSelectedAudioArtifacts] = useState([]);
    const [aartiConfig, setAartiConfig] = useState([]);

    // Entity lists
    const [waypoints, setWaypoints] = useState([]);
    const [saints, setSaints] = useState([]);
    const [deities, setDeities] = useState([]);
    const [journeys, setJourneys] = useState([]);
    const [projects, setProjects] = useState([]);

    // Selected relations
    const [selectedWaypoints, setSelectedWaypoints] = useState([]);
    const [selectedSaints, setSelectedSaints] = useState([]);
    const [selectedDeities, setSelectedDeities] = useState([]);
    const [selectedJourneys, setSelectedJourneys] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);

    // Search terms
    const [searchWaypoint, setSearchWaypoint] = useState('');
    const [searchSaint, setSearchSaint] = useState('');
    const [searchDeity, setSearchDeity] = useState('');
    const [searchJourney, setSearchJourney] = useState('');
    const [searchProject, setSearchProject] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [charCount, setCharCount] = useState({
        stream_title: 0, stream_title_mar: 0, stream_title_hin: 0, stream_title_guj: 0
    });

    useEffect(() => {
        fetchWaypoints();
        fetchSaints();
        fetchDeities();
        fetchJourneys();
        fetchProjects();
    }, []);

    // Pre-populate form and relations from streamData
    useEffect(() => {
        if (!streamData) return;

        const newFormData = {
            stream_type: streamData.stream_type || 'youtube',
            stream_category: streamData.stream_category || 'live_darshan',
            youtube_video_id: streamData.youtube_video_id || '',
            stream_mode: streamData.rtsp_url ? 'rtsp_pull' : 'rtmp_push',
            stream_key: streamData.stream_key || '',
            rtsp_url: streamData.rtsp_url || '',
            hls_url: streamData.hls_url || '',
            stream_title: streamData.stream_title || '',
            stream_title_mar: streamData.stream_title_mar || '',
            stream_title_hin: streamData.stream_title_hin || '',
            stream_title_guj: streamData.stream_title_guj || '',
            thumbnail_url: streamData.thumbnail_url || '',
            is_featured: Boolean(streamData.is_featured),
            enable_multi_view: Boolean(streamData.enableMultiView ?? streamData.enable_multi_view),
            stream_status: streamData.stream_status || 'draft',
            auto_convert: streamData.auto_convert ? 1 : 0,
            converted_artifact_id: streamData.converted_artifact_id || null,
            converted_r2_url: streamData.converted_r2_url || null,
            orientation: streamData.orientation || 'portrait',
            max_quality: streamData.maxQuality || '1080p',
            display_sponsor: Boolean(streamData.displaySponsor),
            sponsor_1_name: streamData.sponsor1Name || '',
            sponsor_1_name_mar: streamData.sponsor1NameMar || '',
            sponsor_1_name_hin: streamData.sponsor1NameHin || '',
            sponsor_1_name_guj: streamData.sponsor1NameGuj || '',
            sponsor_2_name: streamData.sponsor2Name || '',
            sponsor_2_name_mar: streamData.sponsor2NameMar || '',
            sponsor_2_name_hin: streamData.sponsor2NameHin || '',
            sponsor_2_name_guj: streamData.sponsor2NameGuj || '',
            sponsor_1_image: streamData.sponsor1Image || '',
            sponsor_1_info: streamData.sponsor1Info || '',
            sponsor_1_info_mar: streamData.sponsor1InfoMar || '',
            sponsor_1_info_hin: streamData.sponsor1InfoHin || '',
            sponsor_1_info_guj: streamData.sponsor1InfoGuj || '',
            sponsor_2_image: streamData.sponsor2Image || '',
            sponsor_2_info: streamData.sponsor2Info || '',
            sponsor_2_info_mar: streamData.sponsor2InfoMar || '',
            sponsor_2_info_hin: streamData.sponsor2InfoHin || '',
            sponsor_2_info_guj: streamData.sponsor2InfoGuj || '',
            stream_start_at: toISTInput(streamData.streamStartAt || streamData.stream_start_at),
            stream_end_at: toISTInput(streamData.streamEndAt || streamData.stream_end_at),
            original_sound: streamData.originalSound === false ? 0 : 1,
            enable_animation: (streamData.enableAnimation ?? streamData.enable_animation) ? 1 : 0,
            animation_urls: (() => {
                const raw = streamData.animationUrls ?? streamData.animation_urls;
                if (!raw) return [];
                if (Array.isArray(raw)) return raw.filter(Boolean);
                return String(raw).split(',').map(u => u.trim()).filter(Boolean);
            })(),
        };
        setFormData(newFormData);

        // Pre-seed playlist tracks and fetch full artifact list if artifact mode
        const audioArr = Array.isArray(streamData.audio)
            ? streamData.audio
            : (streamData.audio ? [streamData.audio] : []);
        if (streamData.originalSound === false && audioArr.length > 0) {
            setSelectedAudioArtifacts(
                audioArr
                    .filter(a => a && a.artifactId)
                    .map(a => ({ artifact_id: a.artifactId, title: a.title }))
            );
            fetchArtifacts();
        } else {
            setSelectedAudioArtifacts([]);
        }

        // Pre-seed the Aarti config as saved — raw aarti_config passthrough
        // (aarti_id/aarti_audio_artifact_id/icon_playback_*), not the resolved
        // aartiSchedule the app consumes, so it maps 1:1 onto the picker's value.
        setAartiConfig(Array.isArray(streamData.aartiConfig) ? streamData.aartiConfig : []);
        if (streamData.aartiConfig?.length > 0) fetchArtifacts();

        setCharCount({
            stream_title: newFormData.stream_title.length,
            stream_title_mar: newFormData.stream_title_mar.length,
            stream_title_hin: newFormData.stream_title_hin.length,
            stream_title_guj: newFormData.stream_title_guj.length,
        });

        // Pre-populate relations from API response arrays
        if (streamData.relatedWaypoints?.length) {
            setSelectedWaypoints(streamData.relatedWaypoints.map(r => ({ wpt_id: r.wptId, wpt_name: r.wptName })));
        }
        if (streamData.relatedSaints?.length) {
            setSelectedSaints(streamData.relatedSaints.map(r => ({ id: r.id, name: r.name })));
        }
        if (streamData.relatedDeities?.length) {
            setSelectedDeities(streamData.relatedDeities.map(r => ({ deity_id: r.deityId, deity_name: r.deityName })));
        }
        if (streamData.relatedJourneys?.length) {
            setSelectedJourneys(streamData.relatedJourneys.map(r => ({ journey_id: r.journeyId, trip_name: r.tripName })));
        }
        if (streamData.relatedProjects?.length) {
            setSelectedProjects(streamData.relatedProjects.map(r => ({ project_id: r.projectId, project_title: r.projectTitle })));
        }
    }, [streamData]);

    const fetchWaypoints = async () => {
        try {
            const response = await _axios.get(`${ServerPath}/live-darshan/waypoints/list`);
            if (response.data.success === 1) setWaypoints(response.data.data);
        } catch (error) { console.error('Error fetching waypoints:', error); }
    };

    const fetchSaints = async () => {
        try {
            const response = await _axios.get(`${ServerPath}/saints/list/1?limit=1000`);
            if (response.data.success === 1) setSaints(response.data.data);
        } catch (error) { console.error('Error fetching saints:', error); }
    };

    const fetchDeities = async () => {
        try {
            const response = await _axios.get(`${ServerPath}/deity`);
            if (response.data.data) setDeities(response.data.data);
        } catch (error) { console.error('Error fetching deities:', error); }
    };

    const fetchJourneys = async () => {
        try {
            const response = await _axios.get(`${ServerPath}/tripdetails`);
            if (response.data.data) setJourneys(response.data.data);
        } catch (error) { console.error('Error fetching journeys:', error); }
    };

    const fetchProjects = async () => {
        try {
            const response = await _axios.post(`${ServerPath}/projects/`, {
                page: 1, limit: 1000, searchText: '', project_types: [], regions: []
            });
            if (response.data.success === 1) setProjects(response.data.data);
        } catch (error) { console.error('Error fetching projects:', error); }
    };

    const fetchArtifacts = async () => {
        if (artifactsFetched) return;
        try {
            let page = 1, all = [];
            while (true) {
                const res = await _axios.get(`${ServerPath}/artifacts/page/?type=AUDIO_ORALS&page=${page}`);
                const data = res.data.data || [];
                all = [...all, ...data];
                if (data.length < 18) break;
                page++;
            }
            setAudioArtifacts(all);
        } catch (error) { console.error('Error fetching artifacts:', error); }
        setArtifactsFetched(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (['stream_title', 'stream_title_mar', 'stream_title_hin', 'stream_title_guj'].includes(name)) {
            if (value.length > 50) {
                toast.error('Title cannot exceed 50 characters', { toastId: 'length-error' });
                return;
            }
            setCharCount(prev => ({ ...prev, [name]: value.length }));
        }
        if (name === 'stream_category') {
            setFormData(prev => ({
                ...prev,
                stream_category: value,
                ...(prev.stream_type !== 'youtube' ? { original_sound: value === 'live_darshan' ? 1 : 0 } : {}),
            }));
            return;
        }
        if (name === 'stream_start_at') {
            if (formData.stream_end_at && value >= formData.stream_end_at) {
                toast.error('Start time must be before end time');
                return;
            }
        }
        if (name === 'stream_end_at') {
            if (formData.stream_start_at && value <= formData.stream_start_at) {
                toast.error('End time must be after start time');
                return;
            }
        }
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);

            if (!formData.stream_type || !formData.stream_title || !formData.stream_title_guj || !formData.stream_title_hin || !formData.stream_title_mar) {
                toast.error('Please fill in all required fields');
                setIsSubmitting(false);
                return;
            }

            if (formData.stream_type === 'youtube' && !formData.youtube_video_id) {
                toast.error('YouTube video ID is required');
                setIsSubmitting(false);
                return;
            }

            if (formData.stream_type === 'cctv') {
                if (!formData.stream_key) {
                    toast.error('Stream key is required for CCTV streams');
                    setIsSubmitting(false);
                    return;
                }
                if (formData.stream_mode === 'rtsp_pull' && !formData.rtsp_url) {
                    toast.error('RTSP URL is required for RTSP Pull mode');
                    setIsSubmitting(false);
                    return;
                }
            }

            if (formData.stream_type === 'cctv_external' && !formData.hls_url) {
                toast.error('HLS URL is required for External CCTV streams');
                setIsSubmitting(false);
                return;
            }

            if (!formData.thumbnail_url) {
                toast.error('Thumbnail URL is required');
                setIsSubmitting(false);
                return;
            }

            if (!streamData?.id) {
                toast.error('Stream data is missing');
                setIsSubmitting(false);
                return;
            }

            if (formData.stream_start_at && formData.stream_end_at) {
                if (new Date(formData.stream_end_at) <= new Date(formData.stream_start_at)) {
                    toast.error('Stream end time must be after start time');
                    setIsSubmitting(false);
                    return;
                }
            }

            const isCctv = formData.stream_type === 'cctv' || formData.stream_type === 'cctv_external';
            const isAudioApplicable = isCctv;

            if (isAudioApplicable) {
                if (formData.original_sound === 0 && selectedAudioArtifacts.length === 0) {
                    toast.error('At least one audio artifact is required when Original Sound is disabled');
                    setIsSubmitting(false);
                    return;
                }
                if (selectedAudioArtifacts.length > MAX_AUDIO_TRACKS) {
                    toast.error(`A playlist can contain at most ${MAX_AUDIO_TRACKS} audio tracks`);
                    setIsSubmitting(false);
                    return;
                }
            }

            if (isCctv && formData.display_sponsor) {
                if (!formData.sponsor_1_name && !formData.sponsor_2_name) {
                    toast.error('At least one sponsor name (Sponsor 1 or Sponsor 2) is required');
                    setIsSubmitting(false);
                    return;
                }
            }

            const cleanedAnimationUrls = (formData.animation_urls || []).map(u => u.trim()).filter(Boolean);
            if (isCctv && formData.enable_animation === 1) {
                if (cleanedAnimationUrls.length === 0) {
                    toast.error('Add at least one animation URL or disable Live Stream Animation');
                    setIsSubmitting(false);
                    return;
                }
                if (cleanedAnimationUrls.length > MAX_ANIMATIONS) {
                    toast.error(`Maximum ${MAX_ANIMATIONS} animation URLs allowed`);
                    setIsSubmitting(false);
                    return;
                }
            }

            const animationFields = isCctv ? {
                enable_animation: formData.enable_animation ? 1 : 0,
                animation_urls: formData.enable_animation && cleanedAnimationUrls.length ? cleanedAnimationUrls.join(',') : null,
            } : {};

            const sponsorFields = (isCctv && formData.display_sponsor) ? {
                sponsor_1_name: formData.sponsor_1_name || null,
                sponsor_1_name_mar: formData.sponsor_1_name_mar || null,
                sponsor_1_name_hin: formData.sponsor_1_name_hin || null,
                sponsor_1_name_guj: formData.sponsor_1_name_guj || null,
                sponsor_1_image: formData.sponsor_1_image || null,       // ← add
                sponsor_1_info: formData.sponsor_1_info || null,         // ← add
                sponsor_1_info_mar: formData.sponsor_1_info_mar || null, // ← add
                sponsor_1_info_hin: formData.sponsor_1_info_hin || null, // ← add
                sponsor_1_info_guj: formData.sponsor_1_info_guj || null, // ← add
                sponsor_2_name: formData.sponsor_2_name || null,
                sponsor_2_name_mar: formData.sponsor_2_name_mar || null,
                sponsor_2_name_hin: formData.sponsor_2_name_hin || null,
                sponsor_2_name_guj: formData.sponsor_2_name_guj || null,
                sponsor_2_image: formData.sponsor_2_image || null,       // ← add
                sponsor_2_info: formData.sponsor_2_info || null,         // ← add
                sponsor_2_info_mar: formData.sponsor_2_info_mar || null, // ← add
                sponsor_2_info_hin: formData.sponsor_2_info_hin || null, // ← add
                sponsor_2_info_guj: formData.sponsor_2_info_guj || null, // ← add
            } : {};

            const updatePayload = {
                id: streamData.id,
                stream_type: formData.stream_type,
                stream_category: formData.stream_category,
                youtube_video_id: formData.youtube_video_id || null,
                stream_key: formData.stream_key || null,
                rtsp_url: formData.rtsp_url || null,
                hls_url: formData.hls_url || null,
                stream_title: formData.stream_title,
                stream_title_mar: formData.stream_title_mar || null,
                stream_title_hin: formData.stream_title_hin || null,
                stream_title_guj: formData.stream_title_guj || null,
                thumbnail_url: formData.thumbnail_url || null,
                is_featured: formData.is_featured ? 1 : 0,
                enable_multi_view: formData.enable_multi_view ? 1 : 0,
                auto_convert: formData.stream_type === 'cctv' ? formData.auto_convert : 0,
                stream_mode: formData.stream_type === 'cctv' ? formData.stream_mode : null,
                orientation: formData.orientation,
                max_quality: formData.max_quality,
                display_sponsor: isCctv ? (formData.display_sponsor ? 1 : 0) : null,
                stream_start_at: formData.stream_start_at ? formData.stream_start_at.replace('T', ' ') + ':00' : null,
                stream_end_at: formData.stream_end_at ? formData.stream_end_at.replace('T', ' ') + ':00' : null,
                ...(isAudioApplicable ? {
                    original_sound: formData.original_sound,
                    audio_artifact_ids: formData.original_sound === 0 ? selectedAudioArtifacts.map(a => a.artifact_id) : [],
                } : formData.stream_type === 'youtube' ? {
                    original_sound: formData.original_sound,
                } : {}),
                ...sponsorFields,
                ...animationFields,
                waypoint_ids: selectedWaypoints.map(w => w.wpt_id),
                saint_ids: selectedSaints.map(s => s.id),
                deity_ids: selectedDeities.map(d => d.deity_id),
                journey_ids: selectedJourneys.map(j => j.journey_id),
                project_ids: selectedProjects.map(p => p.project_id),
                aarti_config: selectedWaypoints.length > 0 ? aartiConfig : [],
            };

            const response = await _axios.post(`${ServerPath}/live-darshan/update`, updatePayload);

            if (response.data.success === 1) {
                if (formData.stream_type === 'youtube' && formData.stream_status !== streamData.stream_status) {
                    await _axios.post(`${ServerPath}/live-darshan/update-status`, {
                        stream_id: streamData.id,
                        stream_status: formData.stream_status,
                    });
                }
                toast.success('Stream updated successfully');
                setTimeout(() => { onHide(); window.location.reload(); }, 1500);
            } else {
                toast.error(response.data.message || 'Failed to update stream');
            }
        } catch (error) {
            console.error('Error updating stream:', error);
            if (error.response?.status === 409 && error.response?.data?.message) {
                toast.error(error.response.data.message, { autoClose: 6000 });
            } else {
                toast.error(error.response?.data?.message || 'Error updating stream');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStreamTypeBadgeColor = () => {
        if (formData.stream_type === 'youtube') return 'danger';
        if (formData.stream_type === 'cctv_external') return 'warning';
        return 'info';
    };

    const getStreamTypeText = () => {
        if (formData.stream_type === 'youtube') return 'YouTube';
        if (formData.stream_type === 'cctv_external') return 'CCTV – External Server';
        return 'CCTV – Internal Server';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const inputStyle = { height: '46px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', borderRadius: '6px' };
    const labelStyle = { color: '#4A5568', fontWeight: '500', fontSize: '16px' };
    const isCctv = formData.stream_type === 'cctv' || formData.stream_type === 'cctv_external';
    const isAudioApplicable = isCctv;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered style={{ borderRadius: '10px' }}>
            <div style={{ borderRadius: '10px' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0', padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Modal.Title style={{ fontWeight: '600', fontSize: '22px' }}>Edit Live Stream</Modal.Title>
                            <Badge bg={getStreamTypeBadgeColor()}>{getStreamTypeText()}</Badge>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '40px' }}>
                            <span style={{ color: '#666', fontSize: '14px', fontWeight: '500' }}>Last Update On:</span>
                            <span style={{ color: '#999', fontSize: '14px' }}>{formatDate(streamData?.updatedAt || streamData?.updated_at)}</span>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px' }}>
                    <Form>
                        {/* Row 1: Stream Type (read-only) | Stream Category | Orientation | Current Status */}
                        <Row className='mb-3'>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="mb-2" style={labelStyle}>Stream Type</Form.Label>
                                    <Form.Control type="text" value={getStreamTypeText()} disabled className="bg-light"
                                        style={{ ...inputStyle, cursor: 'not-allowed' }} />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Stream Category <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select name="stream_category" value={formData.stream_category} onChange={handleInputChange} style={inputStyle}>
                                        {STREAM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Orientation <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select name="orientation" value={formData.orientation} onChange={handleInputChange} style={inputStyle}>
                                        <option value="portrait">Portrait</option>
                                        <option value="landscape">Landscape</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        {formData.stream_type === 'youtube' ? 'Status' : 'Current Status'}
                                    </Form.Label>
                                    {formData.stream_type === 'youtube' ? (
                                        <Form.Select name="stream_status" value={formData.stream_status} onChange={handleInputChange} style={inputStyle}>
                                            <option value="draft">Draft</option>
                                            <option value="live_now">Live Now</option>
                                            <option value="recently_live">Recently Live</option>
                                            <option value="archived">Archived</option>
                                        </Form.Select>
                                    ) : (
                                        <div style={{ paddingTop: '8px' }}>
                                            {(() => {
                                                const config = getStreamStatusBadgeConfig(formData.stream_status);
                                                return (
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '6px 16px',
                                                        borderRadius: '20px',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        backgroundColor: config.bg,
                                                        color: '#FFFFFF'
                                                    }}>
                                                        {config.label}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Row 2: Titles */}
                        <Row className='mb-3'>
                            {[
                                { name: 'stream_title', placeholder: 'English', label: 'Stream Title (English)' },
                                { name: 'stream_title_mar', placeholder: 'मराठी', label: 'Stream Title (Marathi)' },
                                { name: 'stream_title_hin', placeholder: 'हिन्दी', label: 'Stream Title (Hindi)' },
                                { name: 'stream_title_guj', placeholder: 'ગુજરાતી', label: 'Stream Title (Gujarati)' },
                            ].map(field => (
                                <Col md={3} key={field.name}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="mb-2" style={labelStyle}>
                                            {field.label} <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div className="position-relative">
                                            <Form.Control type="text" name={field.name} value={formData[field.name]}
                                                onChange={handleInputChange} placeholder={field.placeholder}
                                                style={{ ...inputStyle, overflow: 'hidden', textOverflow: 'ellipsis' }} />
                                            <div style={{ position: 'absolute', bottom: '0px', right: '12px', fontSize: '11px', color: '#9CA3AF' }}>
                                                {charCount[field.name]}/50
                                            </div>
                                        </div>
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>



                        {/* YouTube fields */}
                        {formData.stream_type === 'youtube' && (
                            <>
                                <Row className='mb-3'>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                YouTube Video ID <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="youtube_video_id" value={formData.youtube_video_id}
                                                onChange={handleInputChange} placeholder="e.g., dQw4w9WgXcQ" style={inputStyle} />
                                            <Form.Text className="text-muted">
                                                (e.g., youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong>)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                Thumbnail URL <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="thumbnail_url" value={formData.thumbnail_url}
                                                onChange={handleInputChange} placeholder="https://example.com/thumbnail.jpg" style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Check type="checkbox" label="Featured Stream" name="is_featured"
                                            checked={formData.is_featured} onChange={handleInputChange} style={{ fontSize: '16px' }} />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Check type="checkbox" label="Enable Multi View" name="enable_multi_view"
                                            checked={formData.enable_multi_view} onChange={handleInputChange} style={{ fontSize: '16px' }}
                                            title="Allows this stream to be selected when creating a Multi View group." />
                                    </Col>
                                </Row>
                                <Row className='mb-3' style={{ alignItems: 'flex-end' }}>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream Start</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_start_at' value={formData.stream_start_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    {/* <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream End</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_end_at' value={formData.stream_end_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col> */}
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Max Quality</Form.Label>
                                            <Form.Select name='max_quality' value={formData.max_quality}
                                                onChange={handleInputChange} style={inputStyle}>
                                                {['360p', '480p', '720p', '1080p', '1440p', '4K'].map(q => (
                                                    <option key={q} value={q}>{q}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} style={{ display: 'flex', alignItems: 'center', paddingBottom: '11px' }}>
                                        <Form.Check type='checkbox' label='Original Sound'
                                            checked={formData.original_sound === 1}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                original_sound: e.target.checked ? 1 : 0,
                                            }))}
                                            style={{ fontSize: '16px' }} />
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* CCTV Internal fields */}
                        {formData.stream_type === 'cctv' && (
                            <>
                                <Row className='mb-3'>
                                    <Col md={12}>
                                        <Form.Label className="mb-2" style={labelStyle}>Stream Mode</Form.Label>
                                        <div className="d-flex gap-4">
                                            {[
                                                { value: 'rtmp_push', title: 'RTMP Push', badge: '3–5s latency', badgeBg: '#E8F5E9', badgeColor: '#2E7D32', desc: 'NVR camera pushes stream to our server.' },
                                                { value: 'rtsp_pull', title: 'RTSP Pull', badge: '15–30s latency', badgeBg: '#FFF3E0', badgeColor: '#E65100', desc: 'Our server pulls stream from camera RTSP URL.' },
                                            ].map(mode => (
                                                <div key={mode.value} style={{
                                                    flex: 1, padding: '14px 18px', borderRadius: '10px',
                                                    border: `2px solid ${formData.stream_mode === mode.value ? '#5662F6' : '#E2E8F0'}`,
                                                    background: formData.stream_mode === mode.value ? '#F0F1FF' : '#F8F9FA',
                                                    opacity: formData.stream_mode === mode.value ? 1 : 0.5,
                                                    cursor: 'not-allowed',
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: `2px solid ${formData.stream_mode === mode.value ? '#5662F6' : '#CBD5E0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {formData.stream_mode === mode.value && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#5662F6' }} />}
                                                        </div>
                                                        <span style={{ fontWeight: '600', fontSize: '14px', color: formData.stream_mode === mode.value ? '#5662F6' : '#A0AEC0' }}>{mode.title}</span>
                                                        <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', background: mode.badgeBg, color: mode.badgeColor }}>{mode.badge}</span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#718096', paddingLeft: '26px' }}>{mode.desc}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>

                                <Row className='mb-3'>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                Stream Key <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="stream_key" value={formData.stream_key}
                                                onChange={handleInputChange} placeholder="e.g., somnath-darshan-1" style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                Thumbnail URL <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="thumbnail_url" value={formData.thumbnail_url}
                                                onChange={handleInputChange} placeholder="https://example.com/thumbnail.jpg" style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {formData.stream_mode === 'rtsp_pull' && (
                                    <Row className='mb-3'>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="mb-2" style={labelStyle}>RTSP URL</Form.Label>
                                                <Form.Control type="text" name="rtsp_url" value={formData.rtsp_url}
                                                    onChange={handleInputChange} placeholder="rtsp://user:pass@ip:port/path" style={inputStyle} />
                                            </Form.Group>
                                        </Col>
                                        {streamData?.hls_url && (
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="mb-2" style={labelStyle}>Current HLS URL</Form.Label>
                                                    <Form.Control type="text" value={streamData.hls_url} disabled className="bg-light"
                                                        style={{ ...inputStyle, fontSize: '12px' }} />
                                                    <Form.Text className="text-muted">Auto-generated HLS URL (read-only)</Form.Text>
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                )}

                                <Row className='mb-3'>
                                    <Col md={12}>
                                        <div style={{
                                            padding: '14px 18px',
                                            borderRadius: '10px',
                                            border: '1px solid #E2E8F0',
                                            background: '#FAFAFA',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#4A5568', marginBottom: '3px' }}>
                                                    Auto Convert to Recording
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#718096' }}>
                                                    When enabled, the stream will be automatically saved as a recorded video when it ends.
                                                </div>
                                            </div>
                                            <Form.Check
                                                type='switch'
                                                id='edit_auto_convert_switch'
                                                checked={formData.auto_convert === 1}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    auto_convert: e.target.checked ? 1 : 0
                                                }))}
                                                style={{ fontSize: '16px' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                {streamData?.stream_status === 'recently_live' &&
                                    streamData?.converted_artifact_id && (
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="mb-2" style={labelStyle}>
                                                        Converted Recording
                                                    </Form.Label>
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #BFDBFE',
                                                        background: '#EFF6FF',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}>
                                                        <span style={{ fontSize: '13px', color: '#1D4ED8', fontWeight: '600' }}>
                                                            Artifact: {streamData.converted_artifact_id}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => window.open(
                                                                `/artifacts/${streamData.converted_artifact_id}`, '_blank'
                                                            )}
                                                            style={{
                                                                background: '#2196F3', color: '#fff',
                                                                border: 'none', borderRadius: '6px',
                                                                padding: '4px 12px', fontSize: '12px',
                                                                cursor: 'pointer', fontWeight: '600'
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    )}

                                {/* <Form.Check type='checkbox' label='Featured Stream' name='is_featured' checked={formData.is_featured} onChange={handleInputChange} /> */}
                                <Row className='mb-3' style={{ alignItems: 'flex-end' }}>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream Start</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_start_at' value={formData.stream_start_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream End</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_end_at' value={formData.stream_end_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Max Quality</Form.Label>
                                            <Form.Select name='max_quality' value={formData.max_quality}
                                                onChange={handleInputChange} style={inputStyle}>
                                                {['360p', '480p', '720p', '1080p', '1440p', '4K'].map(q => (
                                                    <option key={q} value={q}>{q}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} style={{ display: 'flex', alignItems: 'center', paddingBottom: '11px' }}>
                                        <Form.Check type='checkbox' label='Display Sponsor' name='display_sponsor'
                                            checked={formData.display_sponsor} onChange={handleInputChange} style={{ fontSize: '16px' }} />
                                    </Col>
                                </Row>
                                <Row className='mb-3'>
                                    <Col md={3} style={{ display: 'flex', alignItems: 'center' }}>
                                        <Form.Check type='checkbox' label='Enable Multi View' name='enable_multi_view'
                                            checked={formData.enable_multi_view} onChange={handleInputChange} style={{ fontSize: '16px' }}
                                            title='Allows this stream to be selected when creating a Multi View group.' />
                                    </Col>
                                </Row>

                                <AnimationSection formData={formData} setFormData={setFormData} inputStyle={inputStyle} labelStyle={labelStyle} />

                                {formData.display_sponsor && (
                                    <>
                                        {/* Sponsor 1 Names */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_1_name', placeholder: 'English', label: 'Sponsor 1 Name', required: true },
                                                { name: 'sponsor_1_name_mar', placeholder: 'मराठी', label: 'Sponsor 1 Name (Marathi)' },
                                                { name: 'sponsor_1_name_hin', placeholder: 'हिन्दी', label: 'Sponsor 1 Name (Hindi)' },
                                                { name: 'sponsor_1_name_guj', placeholder: 'ગુજરાતી', label: 'Sponsor 1 Name (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className="mb-2" style={labelStyle}>
                                                            {f.label}{f.required && <span className="text-danger"> *</span>}
                                                        </Form.Label>
                                                        <Form.Control type="text" name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder} style={inputStyle} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 1 Image */}
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className='mb-2' style={labelStyle}>Sponsor 1 Image URL</Form.Label>
                                                    <Form.Control type='text' name='sponsor_1_image' value={formData.sponsor_1_image}
                                                        onChange={handleInputChange} placeholder='https://cdn.example.com/sponsor1.webp' style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Sponsor 1 Info */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_1_info', placeholder: 'English', label: 'Sponsor 1 Info (English)' },
                                                { name: 'sponsor_1_info_mar', placeholder: 'मराठी', label: 'Sponsor 1 Info (Marathi)' },
                                                { name: 'sponsor_1_info_hin', placeholder: 'हिन्दी', label: 'Sponsor 1 Info (Hindi)' },
                                                { name: 'sponsor_1_info_guj', placeholder: 'ગુજराती', label: 'Sponsor 1 Info (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control as='textarea' rows={3} name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder}
                                                            style={{ border: '1px solid #E2E8F0', borderRadius: '6px' }} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 2 Names */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_2_name', placeholder: 'English', label: 'Sponsor 2 Name' },
                                                { name: 'sponsor_2_name_mar', placeholder: 'मराठी', label: 'Sponsor 2 Name (Marathi)' },
                                                { name: 'sponsor_2_name_hin', placeholder: 'हिन्दी', label: 'Sponsor 2 Name (Hindi)' },
                                                { name: 'sponsor_2_name_guj', placeholder: 'ગુજरातી', label: 'Sponsor 2 Name (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className="mb-2" style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control type="text" name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder} style={inputStyle} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 2 Image */}
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className='mb-2' style={labelStyle}>Sponsor 2 Image URL</Form.Label>
                                                    <Form.Control type='text' name='sponsor_2_image' value={formData.sponsor_2_image}
                                                        onChange={handleInputChange} placeholder='https://cdn.example.com/sponsor2.webp' style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Sponsor 2 Info */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_2_info', placeholder: 'English', label: 'Sponsor 2 Info (English)' },
                                                { name: 'sponsor_2_info_mar', placeholder: 'मराठी', label: 'Sponsor 2 Info (Marathi)' },
                                                { name: 'sponsor_2_info_hin', placeholder: 'हिन्दी', label: 'Sponsor 2 Info (Hindi)' },
                                                { name: 'sponsor_2_info_guj', placeholder: 'ગુજरातી', label: 'Sponsor 2 Info (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control as='textarea' rows={3} name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder}
                                                            style={{ border: '1px solid #E2E8F0', borderRadius: '6px' }} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>
                                    </>
                                )}
                                {isAudioApplicable && (
                                    <AudioSection
                                        formData={formData}
                                        setFormData={setFormData}
                                        audioArtifacts={audioArtifacts}
                                        searchArtifact={searchArtifact}
                                        setSearchArtifact={setSearchArtifact}
                                        onFetchArtifacts={fetchArtifacts}
                                        artifactsFetched={artifactsFetched}
                                        selectedAudioArtifacts={selectedAudioArtifacts}
                                        setSelectedAudioArtifacts={setSelectedAudioArtifacts}
                                    />
                                )}
                            </>
                        )}

                        {/* CCTV External fields */}
                        {formData.stream_type === 'cctv_external' && (
                            <>
                                <Row className='mb-3'>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                HLS Stream URL <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="hls_url" value={formData.hls_url}
                                                onChange={handleInputChange} placeholder="https://example.com/stream/playlist.m3u8" style={inputStyle} />
                                            <Form.Text className="text-muted">You can update the HLS URL if the provider link has changed.</Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-2" style={labelStyle}>
                                                Thumbnail URL <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control type="text" name="thumbnail_url" value={formData.thumbnail_url}
                                                onChange={handleInputChange} placeholder="https://example.com/thumbnail.jpg" style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* <Form.Check type='checkbox' label='Featured Stream' name='is_featured' checked={formData.is_featured} onChange={handleInputChange} /> */}
                                <Row className='mb-3' style={{ alignItems: 'flex-end' }}>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream Start</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_start_at' value={formData.stream_start_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Stream End</Form.Label>
                                            <Form.Control type='datetime-local' name='stream_end_at' value={formData.stream_end_at}
                                                onChange={handleInputChange} style={inputStyle} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label className='mb-2' style={labelStyle}>Max Quality</Form.Label>
                                            <Form.Select name='max_quality' value={formData.max_quality}
                                                onChange={handleInputChange} style={inputStyle}>
                                                {['360p', '480p', '720p', '1080p', '1440p', '4K'].map(q => (
                                                    <option key={q} value={q}>{q}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} style={{ display: 'flex', alignItems: 'center', paddingBottom: '11px' }}>
                                        <Form.Check type='checkbox' label='Display Sponsor' name='display_sponsor'
                                            checked={formData.display_sponsor} onChange={handleInputChange} style={{ fontSize: '16px' }} />
                                    </Col>
                                    <Col md={2} style={{ display: 'flex', alignItems: 'center', paddingBottom: '11px' }}>
                                        <Form.Check type='checkbox' label='Enable Multi View' name='enable_multi_view'
                                            checked={formData.enable_multi_view} onChange={handleInputChange} style={{ fontSize: '16px' }}
                                            title='Allows this stream to be selected when creating a Multi View group.' />
                                    </Col>
                                </Row>

                                <AnimationSection formData={formData} setFormData={setFormData} inputStyle={inputStyle} labelStyle={labelStyle} />

                                {formData.display_sponsor && (
                                    <>
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_1_name', placeholder: 'English', label: 'Sponsor 1 Name', required: true },
                                                { name: 'sponsor_1_name_mar', placeholder: 'मराठी', label: 'Sponsor 1 Name (Marathi)', required: false },
                                                { name: 'sponsor_1_name_hin', placeholder: 'हिन्दी', label: 'Sponsor 1 Name (Hindi)', required: false },
                                                { name: 'sponsor_1_name_guj', placeholder: 'ગુજરાતી', label: 'Sponsor 1 Name (Gujarati)', required: false },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>
                                                            {f.label}{f.required && <span className='text-danger'> *</span>}
                                                        </Form.Label>
                                                        <Form.Control type='text' name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder} style={inputStyle} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 1 Image */}
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className='mb-2' style={labelStyle}>Sponsor 1 Image URL</Form.Label>
                                                    <Form.Control type='text' name='sponsor_1_image' value={formData.sponsor_1_image}
                                                        onChange={handleInputChange} placeholder='https://cdn.example.com/sponsor1.webp' style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Sponsor 1 Info */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_1_info', placeholder: 'English', label: 'Sponsor 1 Info (English)' },
                                                { name: 'sponsor_1_info_mar', placeholder: 'मराठी', label: 'Sponsor 1 Info (Marathi)' },
                                                { name: 'sponsor_1_info_hin', placeholder: 'हिन्दी', label: 'Sponsor 1 Info (Hindi)' },
                                                { name: 'sponsor_1_info_guj', placeholder: 'ગુજરાતી', label: 'Sponsor 1 Info (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control as='textarea' rows={3} name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder}
                                                            style={{ border: '1px solid #E2E8F0', borderRadius: '6px' }} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 2 Names */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_2_name', placeholder: 'English', label: 'Sponsor 2 Name' },
                                                { name: 'sponsor_2_name_mar', placeholder: 'मराठी', label: 'Sponsor 2 Name (Marathi)' },
                                                { name: 'sponsor_2_name_hin', placeholder: 'हिन्दी', label: 'Sponsor 2 Name (Hindi)' },
                                                { name: 'sponsor_2_name_guj', placeholder: 'ગુજરાતી', label: 'Sponsor 2 Name (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control type='text' name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder} style={inputStyle} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Sponsor 2 Image */}
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className='mb-2' style={labelStyle}>Sponsor 2 Image URL</Form.Label>
                                                    <Form.Control type='text' name='sponsor_2_image' value={formData.sponsor_2_image}
                                                        onChange={handleInputChange} placeholder='https://cdn.example.com/sponsor2.webp' style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Sponsor 2 Info */}
                                        <Row className='mb-3'>
                                            {[
                                                { name: 'sponsor_2_info', placeholder: 'English', label: 'Sponsor 2 Info (English)' },
                                                { name: 'sponsor_2_info_mar', placeholder: 'मराठी', label: 'Sponsor 2 Info (Marathi)' },
                                                { name: 'sponsor_2_info_hin', placeholder: 'हिन्दी', label: 'Sponsor 2 Info (Hindi)' },
                                                { name: 'sponsor_2_info_guj', placeholder: 'ગુજरாતી', label: 'Sponsor 2 Info (Gujarati)' },
                                            ].map(f => (
                                                <Col md={3} key={f.name}>
                                                    <Form.Group>
                                                        <Form.Label className='mb-2' style={labelStyle}>{f.label}</Form.Label>
                                                        <Form.Control as='textarea' rows={3} name={f.name} value={formData[f.name]}
                                                            onChange={handleInputChange} placeholder={f.placeholder}
                                                            style={{ border: '1px solid #E2E8F0', borderRadius: '6px' }} />
                                                    </Form.Group>
                                                </Col>
                                            ))}
                                        </Row>
                                    </>
                                )}
                                {isAudioApplicable && (
                                    <AudioSection
                                        formData={formData}
                                        setFormData={setFormData}
                                        audioArtifacts={audioArtifacts}
                                        searchArtifact={searchArtifact}
                                        setSearchArtifact={setSearchArtifact}
                                        onFetchArtifacts={fetchArtifacts}
                                        artifactsFetched={artifactsFetched}
                                        selectedAudioArtifacts={selectedAudioArtifacts}
                                        setSelectedAudioArtifacts={setSelectedAudioArtifacts}
                                    />
                                )}
                            </>
                        )}

                        {/* Relations section */}
                        <hr style={{ margin: '8px 0 20px', borderColor: '#E2E8F0' }} />
                        <div style={{ marginBottom: '12px' }}>
                            <span style={{ fontWeight: '600', fontSize: '15px', color: '#4A5568' }}>Related Entities</span>
                            <span style={{ fontSize: '13px', color: '#A0AEC0', marginLeft: '8px' }}>(optional — link this stream to teerthas, saints, deities, parikramas or projects)</span>
                        </div>

                        <RelationPicker
                            label="Related Temple/Teerthas"
                            searchValue={searchWaypoint} onSearchChange={setSearchWaypoint}
                            results={waypoints} idKey="wpt_id" nameKey="wpt_name"
                            selected={selectedWaypoints}
                            onSelect={w => setSelectedWaypoints(prev => [...prev, w])}
                            onRemove={id => setSelectedWaypoints(prev => prev.filter(w => w.wpt_id !== id))}
                        />

                        {/* Scheduled Aarti playback — driven by the first tagged Temple/Teertha */}
                        <AartiSchedulePicker
                            wptId={selectedWaypoints[0]?.wpt_id || null}
                            audioArtifacts={audioArtifacts}
                            artifactsFetched={artifactsFetched}
                            onFetchArtifacts={fetchArtifacts}
                            value={aartiConfig}
                            onChange={setAartiConfig}
                        />
                        <RelationPicker
                            label="Related Saints"
                            searchValue={searchSaint} onSearchChange={setSearchSaint}
                            results={saints} idKey="id" nameKey="name"
                            selected={selectedSaints}
                            onSelect={s => setSelectedSaints(prev => [...prev, s])}
                            onRemove={id => setSelectedSaints(prev => prev.filter(s => s.id !== id))}
                        />
                        <RelationPicker
                            label="Related Deities"
                            searchValue={searchDeity} onSearchChange={setSearchDeity}
                            results={deities} idKey="deity_id" nameKey="deity_name"
                            selected={selectedDeities}
                            onSelect={d => setSelectedDeities(prev => [...prev, d])}
                            onRemove={id => setSelectedDeities(prev => prev.filter(d => d.deity_id !== id))}
                        />
                        <RelationPicker
                            label="Related Parikramas"
                            searchValue={searchJourney} onSearchChange={setSearchJourney}
                            results={journeys} idKey="journey_id" nameKey="trip_name"
                            selected={selectedJourneys}
                            onSelect={j => setSelectedJourneys(prev => [...prev, j])}
                            onRemove={id => setSelectedJourneys(prev => prev.filter(j => j.journey_id !== id))}
                        />
                        <RelationPicker
                            label="Related Projects"
                            searchValue={searchProject} onSearchChange={setSearchProject}
                            results={projects} idKey="project_id" nameKey="project_title"
                            selected={selectedProjects}
                            onSelect={p => setSelectedProjects(prev => [...prev, p])}
                            onRemove={id => setSelectedProjects(prev => prev.filter(p => p.project_id !== id))}
                        />
                    </Form>
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="secondary" onClick={onHide} className="me-2"
                        style={{ borderRadius: '6px', padding: '10px 24px', fontWeight: '500', fontSize: '16px' }}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}
                        style={{ backgroundColor: '#5662F6', borderColor: '#5662F6', borderRadius: '6px', padding: '10px 40px', fontWeight: '500', fontSize: '16px' }}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default EditLiveStream;

